/**
 * AIWorkpad — a slide-in drawer for lesson editors.
 *
 * Freeform prompt → generates either a text section (rich_text /
 * takeaways / checklist / poll / presentation) or an image (Nano Banana).
 * The generated output is shown inline with a one-click "Copy" button
 * so admins can paste it into whichever section they're editing.
 *
 * The drawer is section-agnostic — it just talks to the two AI endpoints
 * and hands back structured output. Individual section editors that want
 * inline AI can also call the same endpoints directly.
 */
import React, { useContext, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../../App';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Sparkles, Copy, Check, Image as ImageIcon, X, Loader2, Wand2 } from 'lucide-react';

const SECTION_TYPES = [
  { value: 'rich_text',    label: 'Rich text / HTML' },
  { value: 'presentation', label: 'Presentation slides' },
  { value: 'takeaways',    label: 'Key takeaways' },
  { value: 'checklist',    label: 'Checklist' },
  { value: 'poll',         label: 'Poll' },
  { value: 'quiz',         label: 'Quiz' },
];

const AIWorkpad = ({ open, onClose, lessonId }) => {
  const { API } = useContext(AuthContext);
  const [mode, setMode] = useState('text'); // text | image
  const [sectionType, setSectionType] = useState('rich_text');
  const [prompt, setPrompt] = useState('');
  const [aspect, setAspect] = useState('landscape');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  if (!open) return null;

  const generate = async () => {
    if (!prompt.trim()) { toast.error('Enter a prompt first.'); return; }
    setLoading(true);
    setResult(null);
    try {
      if (mode === 'image') {
        const { data } = await axios.post(`${API}/admin/ai/section-image`, {
          prompt, lesson_id: lessonId, aspect,
        });
        setResult({ kind: 'image', url: data.url });
      } else {
        const { data } = await axios.post(`${API}/admin/ai/section-content`, {
          lesson_id: lessonId, section_type: sectionType, prompt,
        });
        setResult({ kind: 'text', section_type: data.section_type, content: data.content });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Generation failed.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    let text = '';
    if (result?.kind === 'image') text = result.url;
    else if (result?.content) text = JSON.stringify(result.content, null, 2);
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      toast.success('Copied to clipboard.');
      setTimeout(() => setCopied(false), 1500);
    });
  };

  // "Copy formatted HTML" — copies BOTH text/plain (raw HTML string) AND
  // text/html (parsed rich content) so that:
  //   • Pasting into the RichTextEditor (a contentEditable) lands as
  //     styled paragraphs / headings — not as raw <p> tags.
  //   • Pasting into a plain textarea still works and gives the raw HTML.
  // Uses the modern Clipboard API when available, falls back to a hidden
  // execCommand copy otherwise (Safari, older browsers).
  const copyFormattedHtml = async () => {
    const html = (result?.content && result.content.html) || '';
    if (!html) return;
    try {
      if (window.ClipboardItem && navigator.clipboard?.write) {
        const item = new window.ClipboardItem({
          'text/html':  new Blob([html],           { type: 'text/html'  }),
          'text/plain': new Blob([stripHtml(html)], { type: 'text/plain' }),
        });
        await navigator.clipboard.write([item]);
      } else {
        // Fallback: select a hidden div containing the rendered HTML
        // and run execCommand('copy'). This preserves formatting when
        // pasted into rich text targets.
        const holder = document.createElement('div');
        holder.contentEditable = 'true';
        holder.style.position = 'fixed';
        holder.style.opacity = '0';
        holder.style.pointerEvents = 'none';
        holder.innerHTML = html;
        document.body.appendChild(holder);
        const range = document.createRange();
        range.selectNodeContents(holder);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
        document.execCommand('copy');
        sel.removeAllRanges();
        document.body.removeChild(holder);
      }
      setCopied(true);
      toast.success('Formatted HTML copied — paste it into the rich text editor.');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Last resort: raw HTML string only.
      try {
        await navigator.clipboard?.writeText(html);
        toast.success('Copied HTML.');
      } catch {
        toast.error('Copy failed — please select and copy manually.');
      }
    }
  };

  // Strip HTML for the text/plain fallback of the ClipboardItem so pastes
  // into pure-text targets stay clean.
  const stripHtml = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html || '';
    return (tmp.textContent || tmp.innerText || '').trim();
  };

  return (
    <div className="fixed inset-y-0 right-0 z-50 w-full sm:w-[420px] bg-background border-l border-border shadow-2xl overflow-y-auto" data-testid="ai-workpad">
      <div className="p-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Wand2 className="w-5 h-5 text-primary" />
          <div>
            <h3 className="font-heading text-lg font-black">AI Workpad</h3>
            <p className="text-[11px] text-muted-foreground">Generate + copy into any section</p>
          </div>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-muted rounded" data-testid="workpad-close">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="p-4 space-y-4">
        {/* Mode toggle */}
        <div className="flex gap-2">
          <button
            onClick={() => { setMode('text'); setResult(null); }}
            className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold border transition-colors ${mode === 'text' ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border'}`}
            data-testid="mode-text"
          >
            <Sparkles className="w-3.5 h-3.5 inline mr-1" /> Text
          </button>
          <button
            onClick={() => { setMode('image'); setResult(null); }}
            className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold border transition-colors ${mode === 'image' ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border'}`}
            data-testid="mode-image"
          >
            <ImageIcon className="w-3.5 h-3.5 inline mr-1" /> Image (Nano Banana)
          </button>
        </div>

        {mode === 'text' && (
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Section type</label>
            <select
              value={sectionType}
              onChange={(e) => setSectionType(e.target.value)}
              className="w-full mt-1 h-10 px-3 text-sm rounded-md border border-input bg-background"
              data-testid="section-type-select"
            >
              {SECTION_TYPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        )}

        {mode === 'image' && (
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Aspect</label>
            <div className="flex gap-2 mt-1">
              {['landscape', 'square', 'portrait'].map((a) => (
                <button
                  key={a}
                  onClick={() => setAspect(a)}
                  className={`flex-1 px-2 py-1.5 rounded-md text-xs font-bold border transition-colors ${aspect === a ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border'}`}
                  data-testid={`aspect-${a}`}
                >
                  {a}
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Prompt</label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={mode === 'image'
              ? 'e.g. a professional freeze dryer chamber, cross-section diagram, editorial illustration'
              : 'e.g. 5 slides explaining primary drying for a beginner audience'}
            rows={4}
            className="w-full mt-1 text-sm p-3 rounded-lg border border-input bg-background"
            data-testid="workpad-prompt"
          />
        </div>

        <Button
          onClick={generate}
          disabled={loading}
          className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
          data-testid="workpad-generate"
        >
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
          {loading ? 'Generating…' : 'Generate'}
        </Button>

        {/* Result */}
        {result?.kind === 'image' && (
          <div className="space-y-2" data-testid="workpad-result-image">
            <img src={result.url} alt="AI generated" className="w-full rounded-lg border border-border" />
            <Button variant="outline" onClick={copyToClipboard} className="w-full" data-testid="workpad-copy">
              {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
              Copy image URL
            </Button>
            <p className="text-[11px] text-muted-foreground">Paste this URL into any Image section&rsquo;s URL field.</p>
          </div>
        )}
        {result?.kind === 'text' && (
          <div className="space-y-2" data-testid="workpad-result-text">
            {/* When we have HTML, render it visually so the admin sees exactly
                what they're about to paste. Falls back to JSON for section
                types that don't emit HTML (presentations = slides array). */}
            {result.content?.html ? (
              <>
                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Preview</div>
                <div
                  className="prose prose-sm dark:prose-invert max-w-none bg-muted/40 rounded-lg p-3 max-h-[280px] overflow-y-auto border border-border"
                  data-testid="workpad-result-preview"
                  dangerouslySetInnerHTML={{ __html: result.content.html }}
                />
                <Button
                  onClick={copyFormattedHtml}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="workpad-copy-html"
                >
                  {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                  Copy formatted HTML
                </Button>
                <details className="text-[11px] text-muted-foreground">
                  <summary className="cursor-pointer hover:text-foreground">Need the raw JSON envelope?</summary>
                  <pre className="mt-2 text-xs bg-muted rounded-lg p-3 whitespace-pre-wrap break-words max-h-[180px] overflow-y-auto">
                    {JSON.stringify(result.content, null, 2)}
                  </pre>
                  <Button variant="outline" size="sm" onClick={copyToClipboard} className="w-full mt-2" data-testid="workpad-copy">
                    <Copy className="w-3.5 h-3.5 mr-2" /> Copy JSON
                  </Button>
                </details>
                <p className="text-[11px] text-muted-foreground">
                  Paste into any Rich Text editor — headings, lists, and links come through formatted.
                </p>
              </>
            ) : (
              // Non-HTML result (e.g. presentation slides, poll options).
              <>
                <pre className="text-xs bg-muted rounded-lg p-3 whitespace-pre-wrap break-words max-h-[300px] overflow-y-auto">
                  {JSON.stringify(result.content, null, 2)}
                </pre>
                <Button variant="outline" onClick={copyToClipboard} className="w-full" data-testid="workpad-copy">
                  {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                  Copy JSON
                </Button>
                <p className="text-[11px] text-muted-foreground">Paste into the matching field. Presentations use the <code>slides</code> array.</p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AIWorkpad;
